import { createContext, useContext, useEffect, useState } from "react";
import { useJwtToken, useLogin } from "JS/React/Hooks/Users";
import { clearLocalStorage, isBetaEnvironment } from "JS/Helpers";
import { useSnackbar } from "notistack";
import { config } from "JS/Config";
import {
    EventNames,
    EventCategories,
    EventActions,
    ILogin,
    User,
} from "JS/Models";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";
import {
    useGetUserMediaApp,
    useResetGlobalState,
    useGlobalMedia,
} from "../Hooks/Media";
import { messaging } from "JS/Helpers/UserMessaging";
import { useDispatch } from "react-redux";
import { resetDownloadState } from "JS/Redux/download";
import { useUserHistory } from "../Hooks/UserHistory";
import { useMediaEssentials } from "../Hooks/MediaEssentials";
import {
    setLocalStorageUserName,
    updateNgTokens,
} from "JS/Helpers/LocalStorageHelpers";
import { useSyncFirebaseResumeData } from "../Hooks/Firebase/Resume";
import { resetHomeState } from "JS/Redux/Home";
import { setRetContentUpdateRequired } from "JS/Redux/RetiredContent";
import { useVersionUpdate } from "../Hooks/VersionUpdate";
import KeysService from "JS/Services/Keys";

interface ContextType {
    loginStatus: boolean;
    isAuthenticated: boolean;
    loading: boolean;
    sessionLoading: boolean;
    signIn: (loginData: ILogin) => Promise<any>;
    setLoginStatus: (status: boolean) => void;
    redirectedToWebcast: boolean;
    setRedirectedToWebcast: (status: boolean) => void;
    handleSuccessfulLogin: (user: User) => Promise<void>;
}

export interface AuthenticationProviderProps {
    children: React.ReactNode;
    isOnline?: boolean;
}
export const AuthenticationContext = createContext<ContextType | undefined>(
    undefined,
);
export const useAuth = () => useContext(AuthenticationContext);

export const AuthenticationProvider = (props: AuthenticationProviderProps) => {
    const { children, isOnline } = props;

    const { loading, login, loginResponse } = useLogin();
    const { disableJwtToken } = useJwtToken();
    const { enqueueSnackbar } = useSnackbar();
    const { logFirebaseEvent } = useFirebaseLogger();
    const { resetReduxState, resetLoginState } = useResetGlobalState();

    const { refetch: getMediaAppUsers } = useGetUserMediaApp();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loginStatus, setLoginStatus] = useState(true);
    const [sessionLoading, setSessionLoading] = useState(true);
    const { addUser } = useUserHistory();
    const [redirectedToWebcast, setRedirectedToWebcast] = useState(false);
    const { refetch: fetchMediaEssentials } = useMediaEssentials(true);
    const { fetch: fetchVersionUpdate } = useVersionUpdate();
    const { lastLoggedInUser, setLastLoggedInUser } = useGlobalMedia();

    const { syncFirebaseData } = useSyncFirebaseResumeData();
    const dispatch = useDispatch();

    const logout = () => {
        disableJwtToken();
        resetReduxState();
        clearLocalStorage();
    };

    const setUserData = (
        ngAccessToken: string,
        ngRefreshToken: string,
        memberId: string,
    ) => {
        localStorage.setItem("memberId", memberId);
        updateNgTokens(ngAccessToken, ngRefreshToken);
        config.user.memberId = memberId;
        config.ngAccesstoken = ngAccessToken;
        config.ngRefreshToken = ngRefreshToken;
    };

    const loadSession = async () => {
        const token = config.ngAccesstoken;
        if (token && token.length) {
            try {
                setSessionLoading(true);
                await fetchVersionUpdate((e) => {
                    throw e;
                });
                await new KeysService().getKeys();
                await fetchMediaEssentials();
                setIsAuthenticated(true);
            } catch (err) {
                setIsAuthenticated(false);
                logout();
            } finally {
                setSessionLoading(false);
            }
        } else {
            setIsAuthenticated(false);
            setSessionLoading(false);
        }
    };

    const authenticateUser = () => {
        setIsAuthenticated(true);

        logFirebaseEvent(EventNames.LOGGED_IN, {
            action: EventActions.LOGIN,
            category: EventCategories.GENERAL,
        });
        logFirebaseEvent(EventNames.APPLICATION_LAUNCHED, {
            action: EventActions.APPLICATION_LAUNCHED,
            category: EventCategories.GENERAL,
        });
    };

    const handleSuccessfulLogin = async (user: User) => {
        setUserData(user.accessToken, user.refreshToken, user.memberId);
        setLocalStorageUserName(user.firstName, user.lastName);
        handleUserSwitch(user.memberId);
        dispatch(resetHomeState());
        dispatch(resetDownloadState());

        addUser(user);
        await loadSession();
        syncFirebaseData();

        if (isBetaEnvironment()) {
            const data = await getMediaAppUsers();

            if (!!data ? !data.includes(user.memberId) : false) {
                setIsAuthenticated(false);
                enqueueSnackbar(`${messaging?.common?.notAllowedToLink}`, {
                    variant: "warning",
                });
                logout();
            } else {
                authenticateUser();
            }
        } else {
            authenticateUser();
        }
    };

    const handleUserSwitch = (memberId: string) => {
        if (memberId !== lastLoggedInUser) resetLoginState();
        dispatch(setRetContentUpdateRequired());
        setLastLoggedInUser(memberId);
    };

    const signIn = async (loginData: ILogin) => login(loginData);

    useEffect(() => {
        if (loginResponse) handleSuccessfulLogin(loginResponse);
    }, [loginResponse]);

    useEffect(() => {
        if (isOnline) {
            setSessionLoading(true);
            setTimeout(() => loadSession(), 1000);
        }
    }, [isOnline]);

    return (
        <AuthenticationContext.Provider
            value={{
                loginStatus,
                setLoginStatus,
                isAuthenticated,
                loading,
                sessionLoading,
                signIn,
                redirectedToWebcast,
                setRedirectedToWebcast,
                handleSuccessfulLogin,
            }}
        >
            {children}
        </AuthenticationContext.Provider>
    );
};
